import { CookieService } from 'ngx-cookie-service';
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map, tap } from 'rxjs';

import { environment } from '@env/environment';
import { ID } from '@state/base';

import { ClientNotification, DISMISSED_COOKIE_NAME } from './client-notifications.model';
import { ClientNotificationsRepository } from './client-notifications.repository';
import { BannerIndexResponse } from '$interfaces/banner_index/response';
import { BannerIndexRequest } from '$interfaces/banner_index/request';

@Injectable({ providedIn: 'root' })
export class ClientNotificationsService {
  private cookieService = inject(CookieService);
  private http = inject(HttpClient);
  private repository = inject(ClientNotificationsRepository);

  private domain = this.getCookieDomain();

  // RPC: loads the banners configured on the server and stores them as client
  // notifications. Dismissed ids are tracked separately (cookie), so a banner
  // the user already dismissed stays hidden even after a fresh fetch.
  fetchClientNotifications$(): Observable<ClientNotification[]> {
    let url = `${environment.apiUrl}/rpc/banner_index`;
    let body: BannerIndexRequest = {};

    return this.http.post<BannerIndexResponse>(url, body).pipe(
      map(({ banners }) => banners.map(({ id, content }) => ({ id, message: content }))),
      tap({ next: (notifications) => this.repository.setClientNotifications(notifications) })
    );
  }

  dismissNotification(id: ID): void {
    let dismissedIds = this.repository.getDismissedIds();

    if (!dismissedIds.includes(id)) {
      dismissedIds.push(id);
      this.setDismissedIdsCookie(dismissedIds);
      this.repository.setDismissedIds(dismissedIds);
    }
  }

  private setDismissedIdsCookie(ids: ID[]): void {
    this.cookieService.set(DISMISSED_COOKIE_NAME, ids.join(','), {
      domain: this.domain,
      expires: 30,
      path: '/',
      sameSite: 'Strict',
      secure: false
    });
  }

  reset(): void {
    this.repository.reset();
  }

  private getCookieDomain(): string {
    let hostname = window.location.hostname;
    let parts = hostname.split('.');
    // Remove first segment (subdomain like docs, fli, www) if present
    if (parts.length > 2) return parts.slice(1).join('.');
    return hostname;
  }
}
